export interface ToolCall {
  name: string;
  arguments?: Record<string, unknown>;
  result?: string;
  status: "running" | "completed";
}

export type NdjsonEvent =
  | {
      type: "tool_call_start";
      name: string;
      arguments?: Record<string, unknown>;
    }
  | { type: "tool_call_end"; name: string; result: string }
  | { type: "content"; data: string }
  | { type: "escalation"; htmlMessage: string };

export const toolNameMap: Record<string, string> = {
  knowledgeBase: "Knowledge Base",
  currentMedications: "Current Medications",
  medicationInfoForNdc: "Medication Info",
  basicDemographics: "Demographics",
  messages: "Messages",
  userTasks: "Tasks",
  observations: "Observations",
  labOrders: "Lab Orders",
  labOrderResults: "Lab Order Results",
  prescriptions: "Prescriptions",
  notes: "Notes",
  appointments: "Appointments",
  consultations: "Consultations",
  medicalConditions: "Medical Conditions",
  formularySheetInsuranceCoverage: "Insurance Coverage"
};

export const humanizeToolName = (name: string): string =>
  toolNameMap[name] ??
  name.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());

export const formatToolArgs = (
  args: Record<string, unknown> | undefined
): string => {
  if (!args) {
    return "";
  }
  const entries = Object.entries(args);
  if (entries.length === 0) {
    return "";
  }
  return entries.map(([k, v]) => `${k}: ${JSON.stringify(v)}`).join(", ");
};

/**
 * Convert simple HTML (as used in backend escalation messages) to markdown.
 */
export const htmlToMarkdown = (html: string): string =>
  html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>\s*<p>/gi, "\n\n")
    .replace(/<\/?p>/gi, "")
    .replace(/<b>(.*?)<\/b>/gi, "**$1**")
    .replace(/<strong>(.*?)<\/strong>/gi, "**$1**")
    .replace(/<i>(.*?)<\/i>/gi, "*$1*")
    .replace(/<em>(.*?)<\/em>/gi, "*$1*")
    .replace(/<[^>]+>/g, "")
    .trim();

/**
 * Strip embedded escalation JSON from a historical message's content value.
 */
export const stripEscalationJson = (text: string): string => {
  if (!text.startsWith("{")) {
    return text;
  }

  let depth = 0;
  let inString = false;
  let isEscaped = false;
  let jsonEnd = -1;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (isEscaped) {
      isEscaped = false;
      continue;
    }
    if (ch === "\\") {
      isEscaped = true;
      continue;
    }
    if (ch === '"') {
      inString = !inString;
      continue;
    }
    if (inString) {
      continue;
    }
    if (ch === "{") {
      depth++;
    } else if (ch === "}") {
      depth--;
      if (depth === 0) {
        jsonEnd = i + 1;
        break;
      }
    }
  }

  if (jsonEnd === -1) {
    return text;
  }

  const jsonStr = text.slice(0, jsonEnd);
  try {
    const parsed = JSON.parse(jsonStr) as Record<string, unknown>;
    if (!parsed.assistanceEventId) {
      return text;
    }
    const remaining = text.slice(jsonEnd).trim();
    const html =
      (parsed.htmlMessage as string) ?? (parsed.message as string) ?? "";
    const md = html ? htmlToMarkdown(html) : "";
    return [md, remaining].filter(Boolean).join("\n\n");
  } catch {
    return text;
  }
};

/**
 * Try to parse a line as an NDJSON event.
 * Returns the parsed event or `null` if the line is not valid NDJSON.
 */
export const parseNdjsonLine = (line: string): NdjsonEvent | null => {
  const trimmed = line.trim();
  if (!trimmed.startsWith("{")) {
    return null;
  }
  try {
    const parsed = JSON.parse(trimmed) as Record<string, unknown>;
    if (
      parsed.type === "tool_call_start" ||
      parsed.type === "tool_call_end" ||
      parsed.type === "content"
    ) {
      return parsed as unknown as NdjsonEvent;
    }
    if (parsed.assistanceEventId) {
      return {
        type: "escalation",
        htmlMessage:
          (parsed.htmlMessage as string) ?? (parsed.message as string) ?? ""
      };
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * Apply an NDJSON event to a running list of tool calls and accumulated text.
 * Mutates `toolCalls` in place and returns the updated full text.
 */
export const applyNdjsonEvent = (
  event: NdjsonEvent,
  fullText: string,
  toolCalls: ToolCall[]
): string => {
  switch (event.type) {
    case "tool_call_start":
      toolCalls.push({
        name: event.name,
        arguments: event.arguments,
        status: "running"
      });
      return fullText;
    case "tool_call_end": {
      const tc = toolCalls.find(
        (t) => t.name === event.name && t.status === "running"
      );
      if (tc) {
        tc.result = event.result;
        tc.status = "completed";
      }
      return fullText;
    }
    case "content":
      return fullText + event.data;
    case "escalation":
      return fullText + htmlToMarkdown(event.htmlMessage) + "\n\n";
  }
};

export interface StructuredContent {
  text: string;
  toolCalls: ToolCall[];
}

/**
 * Parse structured content from a stored message.
 * When contentType is "application/json", extracts text and tool calls from the JSON.
 * Falls back to plain text with no tool calls for other content types or parse failures.
 */
export const parseStructuredContent = (
  contentType: string | undefined | null,
  contentValue: string | undefined | null
): StructuredContent => {
  const raw = contentValue ?? "";

  if (contentType !== "application/json") {
    return { text: stripEscalationJson(raw), toolCalls: [] };
  }

  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const text = typeof parsed.text === "string" ? parsed.text : raw;
    const toolCalls = Array.isArray(parsed.toolCalls)
      ? (parsed.toolCalls as ToolCall[])
      : [];
    return { text, toolCalls };
  } catch {
    return { text: stripEscalationJson(raw), toolCalls: [] };
  }
};
